import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";

import useAuth from "hooks/useAuth";
import { api } from "services/api";

const StaticDataContext = createContext(null);

const STATUS_URL = "/status/?is_active=true";
const USERS_URL = "/users/";

const extractList = (settled) => {
  if (settled.status !== "fulfilled") return { list: [], error: settled.reason };
  const payload = settled.value?.data;
  return { list: Array.isArray(payload) ? payload : [], error: null };
};

export const StaticDataProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();

  const [ordersStatus, setOrdersStatus] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const inFlightRef = useRef(null);

  const fetchAll = useCallback(async () => {
    if (inFlightRef.current) return inFlightRef.current;

    setLoading(true);
    const promise = Promise.allSettled([
      api.get(STATUS_URL),
      api.get(USERS_URL),
    ]).then(([statusSettled, usersSettled]) => {
      const statusResult = extractList(statusSettled);
      const usersResult = extractList(usersSettled);

      setOrdersStatus(statusResult.list);
      setUsers(usersResult.list);

      const combinedError = statusResult.error || usersResult.error;
      setError(combinedError || null);
      setLoading(false);
      inFlightRef.current = null;
    });

    inFlightRef.current = promise;
    return promise;
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setOrdersStatus([]);
      setUsers([]);
      setError(null);
      return;
    }
    fetchAll();
  }, [isAuthenticated, fetchAll]);

  const value = useMemo(
    () => ({
      ordersStatus,
      users,
      loading,
      error,
      refetch: fetchAll,
    }),
    [ordersStatus, users, loading, error, fetchAll],
  );

  return (
    <StaticDataContext.Provider value={value}>
      {children}
    </StaticDataContext.Provider>
  );
};

StaticDataProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useStaticData = () => {
  const ctx = useContext(StaticDataContext);
  if (!ctx) {
    throw new Error("useStaticData must be used within a StaticDataProvider");
  }
  return ctx;
};

export default StaticDataContext;
